import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { workspacesApi, type Workspace, type WorkspaceRole } from '../workspaces/workspacesApi';

export default function WorkspacesPage() {
  const { user, getToken, refresh, logout } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [inviteWorkspaceId, setInviteWorkspaceId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>('MEMBER');
  const [inviteStatus, setInviteStatus] = useState('');
  const [inviting, setInviting] = useState(false);

  const loadWorkspaces = useCallback(() => {
    setLoading(true);
    workspacesApi
      .list(getToken, refresh)
      .then(data => {
        setWorkspaces(data.workspaces);
        if (data.workspaces.length === 0) setShowCreateForm(true);
      })
      .catch(() => setError('Failed to load workspaces'))
      .finally(() => setLoading(false));
  }, [getToken, refresh]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;
    setCreating(true);
    setError('');
    try {
      await workspacesApi.create(newWorkspaceName.trim(), getToken, refresh);
      setNewWorkspaceName('');
      setShowCreateForm(false);
      loadWorkspaces();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  }

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    if (!inviteWorkspaceId || !inviteEmail.trim()) return;
    setInviting(true);
    setInviteStatus('');
    try {
      await workspacesApi.invite(inviteWorkspaceId, inviteEmail.trim(), inviteRole, getToken, refresh);
      setInviteStatus(`Invited ${inviteEmail.trim()} as ${inviteRole}`);
      setInviteEmail('');
    } catch (err) {
      setInviteStatus(err instanceof Error ? err.message : 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  }

  return (
    <main className="page">
      <header className="toolbar" style={{ justifyContent: 'space-between' }}>
        <h1>Workspaces</h1>
        <span className="toolbar">
          <span className="muted">{user?.displayName}</span>
          <button onClick={logout}>Sign out</button>
        </span>
      </header>

      <div className="toolbar" style={{ justifyContent: 'flex-end', margin: '12px 0' }}>
        {!showCreateForm && (
          <button type="button" onClick={() => setShowCreateForm(true)}>
            + New workspace
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className="form-card">
          {workspaces.length === 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong>Create your first workspace</strong>
              <p className="muted" style={{ margin: '4px 0 0' }}>
                A workspace is your personal hub for all your work. Give it a name — something like
                your name, your company, or a project. You can always create more later, and you
                never have to invite anyone if you're working solo.
              </p>
            </div>
          )}
          <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={newWorkspaceName}
              onChange={e => setNewWorkspaceName(e.target.value)}
              placeholder="Workspace name"
              required
              autoFocus
              style={{ flex: 1 }}
            />
            <div className="form-actions" style={{ marginTop: 0 }}>
              <button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create workspace'}
              </button>
              {workspaces.length > 0 && (
                <button type="button" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {loading && <p className="muted">Loading…</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !showCreateForm && workspaces.length === 0 && <p className="muted">No workspaces yet.</p>}

      <ul className="card-list">
        {workspaces.map(workspace => (
          <li key={workspace.workspaceId} className="card">
            <strong>{workspace.name}</strong>
            <div className="toolbar" style={{ marginTop: 8 }}>
              <Link to={`/workspaces/${workspace.workspaceId}`}>
                <button type="button">Open</button>
              </Link>
              <button onClick={() => { setInviteWorkspaceId(workspace.workspaceId); setInviteStatus(''); }}>
                Invite member
              </button>
            </div>

            {inviteWorkspaceId === workspace.workspaceId && (
              <form onSubmit={handleInvite} style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  style={{ flex: 1 }}
                />
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value as WorkspaceRole)}>
                  <option value="MEMBER">Member</option>
                  <option value="ADMIN">Admin</option>
                </select>
                <button type="submit" disabled={inviting}>
                  {inviting ? 'Inviting…' : 'Invite'}
                </button>
              </form>
            )}
            {inviteWorkspaceId === workspace.workspaceId && inviteStatus && (
              <p className="muted" style={{ marginTop: 8 }}>{inviteStatus}</p>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
